import { useAppStore } from '../../store/store';
import { getJwtExpiration } from '../jwt';

/** sessionStorage flag — keep Header hidden after QS token strip (same pattern as `mobileApp`). */
export const VIEWER_EMBED_STORAGE_KEY = 'viewerEmbed';

type NavigateReplace = (
  to: { pathname: string; search: string },
  options: { replace: boolean },
) => void;

export type ApplyViewerWebviewTokensArgs = {
  pathname: string;
  /** Location search including leading `?`, or empty. */
  search: string;
  navigate: NavigateReplace;
  getUserInfo: (options: { redirectHome: false }) => Promise<void>;
  storage?: Pick<Storage, 'setItem'>;
};

/** True for `/viewer/:fileId` (optional trailing slash). */
export function isViewerFilePath(pathname: string): boolean {
  return /^\/viewer\/[^/]+\/?$/.test(pathname);
}

export function isViewerEmbedSession(storage: Pick<Storage, 'getItem'> = sessionStorage): boolean {
  return storage.getItem(VIEWER_EMBED_STORAGE_KEY) === '1';
}

/**
 * On `/viewer/:fileId` with both `token` + `rToken` QS params: seed Zustand session,
 * set embed flag, strip secrets from URL (replace), bootstrap user without home redirect.
 * Incomplete QS → no side effects. QS overwrites any rehydrated session.
 *
 * @returns true when embed tokens were applied.
 */
export async function applyViewerWebviewTokensFromSearch({
  pathname,
  search,
  navigate,
  getUserInfo,
  storage = sessionStorage,
}: ApplyViewerWebviewTokensArgs): Promise<boolean> {
  if (!isViewerFilePath(pathname)) {
    return false;
  }

  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const token = params.get('token');
  const rToken = params.get('rToken');

  if (!token || !rToken) {
    return false;
  }

  const tokenExpiry = getJwtExpiration(token);
  const rTokenExpiry = getJwtExpiration(rToken);
  const store = useAppStore.getState();

  store.setCustomTokens({
    token,
    rToken,
    tokenExpiry: tokenExpiry || undefined,
    rTokenExpiry: rTokenExpiry || undefined,
  });
  store.setToken(token);

  storage.setItem(VIEWER_EMBED_STORAGE_KEY, '1');

  params.delete('token');
  params.delete('rToken');
  const cleaned = params.toString();
  navigate({ pathname, search: cleaned ? `?${cleaned}` : '' }, { replace: true });

  await getUserInfo({ redirectHome: false });
  return true;
}
